import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import CustomAuthPage from './pages/CustomAuthPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OwnerDashboard from './pages/OwnerDashboard';
import GCDashboard from './pages/GCDashboard';
import ProjectDetails from './pages/ProjectDetails';
import ProjectHub from './pages/ProjectHub';
import JobsPage from './pages/JobsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ContractorDirectory from './pages/ContractorDirectory';
import MobileNav from './components/MobileNav';
import SiteFooterLinks from './components/SiteFooterLinks';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminView, setAdminViewState] = useState(() => localStorage.getItem('adminView') || 'owner');
  const setAdminView = (v) => { setAdminViewState(v); localStorage.setItem('adminView', v); };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="hb-bg min-h-screen flex items-center justify-center">
        <div className="hb-grid-overlay" />
        <img src="/harperbids-logo.png" alt="HarperBids" className="h-20 opacity-60 animate-pulse hb-logo-glow relative z-10" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, adminView, setAdminView }}>
      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={!user ? <CustomAuthPage /> : <Navigate to="/dashboard" />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              user ? (
                user.role === 'admin' ? (
                  adminView === 'gc' ? <GCDashboard key="admin-gc" /> : <OwnerDashboard key="admin-owner" />
                ) : user.role === 'owner' ? (
                  <OwnerDashboard />
                ) : user.role === 'gc' ? (
                  <GCDashboard />
                ) : (
                  <GCDashboard />
                )
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route
            path="/projects/:projectId"
            element={user ? <ProjectDetails /> : <Navigate to="/auth" />}
          />
          <Route
            path="/projects/:projectId/hub"
            element={user ? <ProjectHub /> : <Navigate to="/auth" />}
          />
          <Route
            path="/jobs"
            element={user ? <JobsPage /> : <Navigate to="/auth" />}
          />
          <Route
            path="/contractors"
            element={user ? <ContractorDirectory /> : <Navigate to="/auth" />}
          />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/" element={<Navigate to={user ? '/dashboard' : '/auth'} />} />
        </Routes>
        <SiteFooterLinks />
        <MobileNav />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthContext.Provider>
  );
}

export default App;
